// @ts-expect-error import works fine, not recognized by IDE
import type { ModuleOptionsNormalized, RefreshHandler } from '@sidebase/nuxt-auth/dist/runtime/types';
import { useAuth, useRuntimeConfig } from '#imports';

/**
 * why a custom handler? it handles an exception thrown by auth.refresh() if it returns a 401
 * redirects to /login if a 401 is thrown
 * simplifies refresh intervals
 */
class AuthRefreshHandler implements RefreshHandler {
  /** Result of `useAuth` composable, mostly used for session data/refreshing */
  auth?: ReturnType<typeof useAuth>;

  /** Runtime config is mostly used for getting provider data */
  runtimeConfig?: ModuleOptionsNormalized;

  /** Refetch interval */
  refetchIntervalTimer?: ReturnType<typeof setInterval>;

  /** Refetch interval for local/refresh schema */
  refreshTokenIntervalTimer?: ReturnType<typeof setInterval>;

  private isRefreshing = false;

  /** Because passing `this.visibilityHandler` to `document.addEventHandler` loses `this` context */
  private boundVisibilityHandler: typeof this.visibilityHandler;

  // constructor(public config: DefaultRefreshHandlerConfig) {
  constructor() {
    this.boundVisibilityHandler = this.visibilityHandler.bind(this);
  }

  init(): void {
    this.runtimeConfig = useRuntimeConfig().public.auth;
    this.auth = useAuth();

    // Initial refresh wird vom auth-refresh.client.ts Plugin übernommen (synchron)
    // damit die Auth-Middleware die Session kennt bevor sie prüft

    document.addEventListener('visibilitychange', this.boundVisibilityHandler, false);

    // const { enablePeriodically } = this.config;
    const enablePeriodically = ((this.runtimeConfig.provider.token.maxAgeInSeconds / 4) * 1000) as number | boolean;

    if (enablePeriodically !== false) {
      const intervalTime = enablePeriodically === true ? 1000 : enablePeriodically;
      this.refetchIntervalTimer = setInterval(() => {
        if (this.auth?.data.value) {
          // this.auth.refresh();
          this.secureRefresh();
        }
      }, intervalTime);
    }
  }

  tryInitialRefresh(): void {
    const refreshTokenCookie = useCookie(this.runtimeConfig?.provider.refresh.token.cookieName || 'auth.refreshToken');

    // Kein Access-Token (auth.data.value ist null), aber Refresh-Token vorhanden
    if (!this.auth?.data.value && refreshTokenCookie.value) {
      this.auth?.refresh().catch(() => {
        // Refresh fehlgeschlagen - nichts tun, User muss sich neu einloggen
      });
    }
  }

  secureRefresh(): void {
    if (!navigator.onLine) return;
    if (this.isRefreshing) return;

    this.isRefreshing = true;

    const timeout = setTimeout(() => {
      this.isRefreshing = false;
    }, 30000);

    const done = () => {
      clearTimeout(timeout);
      this.isRefreshing = false;
    };

    const originalTokenCookie = useCookie('auth.originalToken');
    const { rawToken } = useAuthState();

    if (originalTokenCookie.value) {
      // Impersonation active: swap back to original token, refresh, then re-impersonate
      const impersonatedUserUuid = useCookie('auth.impersonatedUserUuid');
      rawToken.value = originalTokenCookie.value;
      originalTokenCookie.value = null;

      this.auth
        ?.refresh()
        .then(async () => {
          const userUuid = impersonatedUserUuid.value;
          if (userUuid) {
            const { reImpersonate } = useImpersonation();
            await reImpersonate();
            originalTokenCookie.value = rawToken.value;
          }
        })
        .catch((e) => {
          if (e.status === 401 || e.status === 403) {
            this.handleExpiredSession();
          }
        })
        .finally(done);
      return;
    }

    this.auth
      ?.refresh()
      .catch((e) => {
        if (e.status === 401 || e.status === 403) {
          this.handleExpiredSession();
        }
      })
      .finally(done);
  }

  handleExpiredSession(): void {
    // redirect: false – we navigate ourselves below; nuxt-auth would otherwise navigate to the absolute
    // current URL (location.origin + pathname) without `external: true`, which navigateTo rejects (NUXT_E2001)
    this.auth?.signOut({ redirect: false }).finally(() => {
      const $nuxt = useNuxtApp();
      flash('warning', {
        title: $nuxt.$i18n.t('note'),
        description: $nuxt.$i18n.t('session.yourSessionExpired'),
      });
      navigateTo('/login', { external: true });
    });
  }

  destroy(): void {
    // Clear visibility handler
    document.removeEventListener('visibilitychange', this.boundVisibilityHandler, false);

    // Clear refetch interval
    clearInterval(this.refetchIntervalTimer);

    // Clear refetch interval
    if (this.refreshTokenIntervalTimer) {
      clearInterval(this.refreshTokenIntervalTimer);
    }

    // Release state
    this.auth = undefined;
    this.runtimeConfig = undefined;
  }

  visibilityHandler(): void {
    // Listen for when the page is visible, if the user switches tabs
    // and makes our tab visible again, re-fetch the session
    if (document.visibilityState !== 'visible') {
      return;
    }

    // Wenn Session aktiv ist, normal refreshen
    if (this.auth?.data.value) {
      this.secureRefresh();
      return;
    }

    // Wenn keine Session, aber Refresh-Token vorhanden → refresh versuchen
    this.tryInitialRefresh();
  }
}

export default new AuthRefreshHandler();
